import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GetPaymentTermsListDocument,
  CreatePaymentTermDocument,
  type GetPaymentTermsListQuery,
  type CreatePaymentTermMutation,
  type CreatePaymentTermMutationVariables,
} from '@/generated/root-types';
import { DataTable } from '@gastrosoft/shared/ui';
import { useNotification } from '@/app/contexts/NotificationContext';
import { useAuth } from '@/app/auth/AuthContext';
import { isSuperAdmin } from '@/utils/roles';
import { GRAPHQL_MAX_PAGE_SIZE } from '@/utils/constants';

interface Row extends Record<string, unknown> {
  id: string;
  name: string;
  days: number;
  isActive: boolean;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export default function PaymentTerms() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDateTime = (iso: string) => new Date(iso).toLocaleString(locale);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [days, setDays] = useState('14');
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);

  const { data, loading, refetch } = useQuery<GetPaymentTermsListQuery>(
    GetPaymentTermsListDocument,
    {
      variables: { first: GRAPHQL_MAX_PAGE_SIZE },
    },
  );

  const [createPt, { loading: creating }] = useMutation<
    CreatePaymentTermMutation,
    CreatePaymentTermMutationVariables
  >(CreatePaymentTermDocument);

  const nodes = data?.paymentTerms?.nodes ?? [];
  const rows: Row[] = nodes.map((n) => ({
    id: n.id,
    name: n.name,
    days: n.days,
    isActive: n.isActive,
    isDefault: n.isDefault,
    createdAt: n.createdAt,
    updatedAt: n.updatedAt,
  }));

  const columns: GridColDef[] = [
    { field: 'name', headerName: t('common.name'), flex: 1, minWidth: 160 },
    { field: 'days', headerName: t('masterData.days'), width: 100 },
    {
      field: 'isActive',
      headerName: t('masterData.active'),
      width: 100,
      valueFormatter: (v: boolean) => (v ? t('common.yes') : t('common.no')),
    },
    {
      field: 'isDefault',
      headerName: t('common.default'),
      width: 100,
      valueFormatter: (v: boolean) => (v ? t('common.yes') : t('common.no')),
    },
    {
      field: 'createdAt',
      headerName: t('common.created'),
      width: 150,
      valueFormatter: (v: string) => formatDateTime(v),
    },
    {
      field: 'updatedAt',
      headerName: t('common.updated'),
      width: 150,
      valueFormatter: (v: string) => formatDateTime(v),
    },
  ];

  const handleCreate = async () => {
    const d = parseInt(days, 10);
    if (Number.isNaN(d)) {
      showError(t('errors.generic'));
      return;
    }
    try {
      const res = await createPt({
        variables: { input: { name, days: d, isActive, isDefault } },
      });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      if (!res.data?.createPaymentTerm?.paymentTerm) {
        showError(t('errors.generic'));
        return;
      }
      showSuccess(t('masterData.created'));
      setOpen(false);
      setName('');
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 180px)' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="h4">{t('masterData.paymentTerms')}</Typography>
        {isSuperAdmin(user) && (
          <Button variant="contained" onClick={() => setOpen(true)}>
            {t('masterData.createTerm')}
          </Button>
        )}
      </Box>

      <DataTable<Row>
        enableMobileCards
        columns={columns}
        data={rows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={rows.length}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        paginationMode="client"
        onRowClick={(row) => navigate(`/payment-terms/${row.id}`)}
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('masterData.createTerm')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label={t('common.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label={t('masterData.days')}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            fullWidth
            type="number"
          />
          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label={t('masterData.active')}
          />
          <FormControlLabel
            control={
              <Switch checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
            }
            label={t('common.default')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating || !name.trim()}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
